import React, { useEffect, useState } from "react";
import { Checkbox } from "rsuite";
import { useLocation, useSearchParams } from "react-router-dom";
import styles from "./GroupsFilter.module.scss";
import axiosInstance, { baseUrl } from "../../axios/axios";
import { useMarketplaceInfo } from "../../hooks/useMarketplaceInfo";

interface GroupsFilterProps {
  className?: string;
  showHeader?: boolean;
  allGroups?: any[];
}

// Function to fetch all groups with marketplace IDs passed as parameters
export const fetchAllGroups = (
  branchMarketplaceId?: string | number,
  marketplaceId?: string | number
) => {
  // Guard against missing marketplace info
  if (!branchMarketplaceId || !marketplaceId) {
    console.warn("Cannot fetch groups: marketplace info not available");
    return Promise.reject(new Error("Missing marketplace IDs"));
  }

  return axiosInstance.get(
    `/rfzo_therapy_reminder_group/branch_marketplace_id/${branchMarketplaceId}/marketplace_id/${marketplaceId}`
  );
};

export default function GroupsFilter({
  className = "",
  showHeader = true,
  allGroups: externalGroups,
}: GroupsFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [localGroups, setLocalGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(
    externalGroups ? false : true
  );
  const [error, setError] = useState<string | null>(null);

  // Use marketplace info hook
  const { branchMarketplaceId, marketplaceId, isReady, isAdmin } =
    useMarketplaceInfo();

  // Use either externally provided groups or fetch them
  const groups = externalGroups || localGroups;

  useEffect(() => {
    // Only fetch when marketplace info is ready and we don't have external groups
    if (!externalGroups && isReady) {
      setIsLoading(true);
      setError(null);
      fetchAllGroups(branchMarketplaceId, marketplaceId)
        .then((res) => setLocalGroups(res.data))
        .catch((err) => {
          console.error("Error fetching groups:", err);
          setError("Greška pri učitavanju lista");
        })
        .finally(() => setIsLoading(false));
    }
  }, [externalGroups, isReady, branchMarketplaceId, marketplaceId]);

  // Show loading state while marketplace info loads
  if (!externalGroups && !isReady) {
    return (
      <div className={`${styles.groupsFilter} ${className}`}>
        {showHeader && <div className={styles.smallHeader}>Liste</div>}
        <div className={styles.loading}>
          Učitavanje informacija o apoteci...
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.groupsFilter} ${className}`}>
      {showHeader && <div className={styles.smallHeader}>Liste</div>}

      {isLoading ? (
        <div className={styles.loading}>Učitavanje...</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : groups && groups.length > 0 ? (
        <>
          {groups.map((group) => {
            const selectedGroups = decodeURIComponent(
              searchParams.get("groups") || ""
            )
              .split(",")
              .filter(Boolean);

            const isSelected = selectedGroups.includes(group.id.toString());

            const handleCheckboxChange = () => {
              const updatedGroups = isSelected
                ? selectedGroups.filter((id) => id !== group.id.toString())
                : [...selectedGroups, group.id.toString()];

              const newSearchParams = new URLSearchParams(location.search);
              if (updatedGroups.length > 0) {
                newSearchParams.set("groups", updatedGroups.join(","));
              } else {
                newSearchParams.delete("groups");
              }
              setSearchParams(newSearchParams.toString());
            };

            return (
              <div key={group.id} className={styles.groupItem}>
                <Checkbox checked={isSelected} onChange={handleCheckboxChange}>
                  {group.name}
                </Checkbox>
              </div>
            );
          })}
        </>
      ) : (
        <div className={styles.empty}>Nema dostupnih lista</div>
      )}
    </div>
  );
}
